import React, { useEffect, useMemo, useRef, useState } from "react";
import SharedBarcodePicker from "../SharedBarcodePicker";
import { getInventoryItems, getItemByBarcode } from "../../services/dataService";
import { listSuppliers, receivePurchase } from "../../services/purchaseService";
import {
  deletePurchaseDraft,
  getPurchaseDraft,
  savePurchaseDraft,
} from "../../services/purchaseDraftService";
import { useToast } from "../../contexts/ToastContext";
import {
  PURCHASE_DRAFT_STORAGE_KEY,
  chooseLatestPurchaseDraft,
  createPurchaseDraftSnapshot,
  parsePurchaseDraftSnapshot,
  reconcilePurchaseDraft,
} from "../../utils/purchaseDraft";
import {
  calculatePurchaseLine,
  getPurchaseLineKey,
  mergePurchaseLine,
  validatePurchaseDraft,
} from "../../utils/purchaseReceiving";
import NewPurchaseItemDialog from "./NewPurchaseItemDialog";
import PurchaseLineTable from "./PurchaseLineTable";
import SupplierManager from "./SupplierManager";

const newSubmissionKey = () =>
  window.crypto?.randomUUID?.() ||
  `purchase-${Date.now()}-${Math.random().toString(16).slice(2)}`;

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString("en-US", {
    minimumFractionDigits: Number(value) % 1 ? 2 : 0,
    maximumFractionDigits: 2,
  });

const normalizeExistingLine = (item) => {
  const itemId = Number(item.itemId ?? item.id);
  const unitId = item.unitId ?? item.productUnitId ?? null;
  const unitName = item.unitName || item.unit_name || "Unit";
  const price = Number(item.price ?? item.selling_price ?? 0);
  return {
    key: `item:${itemId}`,
    itemId,
    productUnitId: unitId === null ? null : Number(unitId),
    barcode: String(item.barcode || "").trim(),
    itemName: item.name || item.productName,
    unitName,
    conversionFactor: Number(item.conversionFactor ?? item.conversion_factor ?? 1),
    quantity: "1",
    unitBuyPrice: "",
    unitSellPrice: price > 0 ? String(price) : "",
    currentUnitSellPrice: price || null,
    isNewItem: false,
  };
};

const inventoryOptions = (items) => (items || []).flatMap((item) => {
  if (item.unit_inventory_enabled && item.product_units?.length) {
    return item.product_units
      .filter((unit) => unit.is_active !== false)
      .map((unit) => ({
        itemId: item.id,
        unitId: unit.id,
        barcode: unit.barcode,
        name: item.name,
        unitName: unit.unit_name,
        price: unit.selling_price,
        conversionFactor: unit.conversion_factor,
      }));
  }
  return [{
    itemId: item.id,
    unitId: null,
    barcode: item.barcode,
    name: item.name,
    unitName: "Unit",
    price: item.price,
    conversionFactor: 1,
  }];
});

export default function PurchaseReceiveView({
  categories = [],
  onInventoryChanged = () => {},
  onReceived = () => {},
}) {
  const showToast = useToast();
  const [supplierId, setSupplierId] = useState("");
  const [barcode, setBarcode] = useState("");
  const [lines, setLines] = useState([]);
  const [scanning, setScanning] = useState(false);
  const [saving, setSaving] = useState(false);
  const [sharedChoice, setSharedChoice] = useState(null);
  const [unknownBarcode, setUnknownBarcode] = useState("");
  const [inventory, setInventory] = useState([]);
  const [manualSearch, setManualSearch] = useState("");
  const [submissionKey, setSubmissionKey] = useState(newSubmissionKey);
  const [restoreBarcodeFocus, setRestoreBarcodeFocus] = useState(false);
  const [draftReady, setDraftReady] = useState(false);
  const [draftStatus, setDraftStatus] = useState("");
  const [supplierError, setSupplierError] = useState("");
  const [lineErrors, setLineErrors] = useState({});
  const barcodeRef = useRef(null);
  const draftSaveTimerRef = useRef(null);
  const pendingDraftSaveRef = useRef(Promise.resolve());
  const draftSaveSequenceRef = useRef(0);

  useEffect(() => {
    let active = true;
    const hydrateDraft = async () => {
      let localDraft = null;
      try {
        localDraft = parsePurchaseDraftSnapshot(
          window.localStorage.getItem(PURCHASE_DRAFT_STORAGE_KEY),
        );
      } catch {
        // Storage can be unavailable in hardened private-browser modes.
      }

      const [inventoryResult, suppliersResult, remoteResult] = await Promise.allSettled([
        getInventoryItems(),
        listSuppliers(),
        getPurchaseDraft(),
      ]);
      if (!active) return;

      const loadedInventory = inventoryResult.status === "fulfilled"
        ? inventoryResult.value || []
        : [];
      const activeSuppliers = suppliersResult.status === "fulfilled"
        ? suppliersResult.value || []
        : [];
      const remoteDraft = remoteResult.status === "fulfilled"
        ? remoteResult.value
        : null;
      const restoredDraft = chooseLatestPurchaseDraft(localDraft, remoteDraft);

      setInventory(loadedInventory);
      if (restoredDraft) {
        setSupplierId(restoredDraft.supplierId);
        setLines(restoredDraft.lines);
        setSubmissionKey(restoredDraft.submissionKey);
        if (inventoryResult.status === "fulfilled" && suppliersResult.status === "fulfilled") {
          const reconciled = reconcilePurchaseDraft({
            draft: restoredDraft,
            inventory: loadedInventory,
            activeSuppliers,
          });
          setSupplierError(reconciled.supplierError);
          setLineErrors(reconciled.lineErrors);
        }
        setDraftStatus("Draft restored");
      }
      setDraftReady(true);
    };
    hydrateDraft();
    return () => {
      active = false;
      window.clearTimeout(draftSaveTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!draftReady) return undefined;
    window.clearTimeout(draftSaveTimerRef.current);
    if (!supplierId && lines.length === 0) {
      setDraftStatus("");
      return undefined;
    }

    const snapshot = createPurchaseDraftSnapshot({
      supplierId,
      submissionKey,
      lines,
    });
    let localDraftSaved = true;
    try {
      window.localStorage.setItem(
        PURCHASE_DRAFT_STORAGE_KEY,
        JSON.stringify(snapshot),
      );
    } catch {
      localDraftSaved = false;
      setDraftStatus("Draft not saved—keep this page open");
    }

    if (localDraftSaved) setDraftStatus("Saving draft…");
    const sequence = draftSaveSequenceRef.current + 1;
    draftSaveSequenceRef.current = sequence;
    draftSaveTimerRef.current = window.setTimeout(() => {
      const request = pendingDraftSaveRef.current
        .catch(() => null)
        .then(() => savePurchaseDraft(snapshot));
      pendingDraftSaveRef.current = request.catch(() => null);
      request
        .then(() => {
          if (draftSaveSequenceRef.current === sequence) {
            setDraftStatus("Draft saved");
          }
        })
        .catch(() => {
          if (draftSaveSequenceRef.current === sequence) {
            setDraftStatus("Draft not saved—keep this page open");
          }
        });
    }, 750);

    return () => window.clearTimeout(draftSaveTimerRef.current);
  }, [draftReady, supplierId, lines, submissionKey]);

  useEffect(() => {
    if (!restoreBarcodeFocus || saving || scanning) return;
    barcodeRef.current?.focus();
    setRestoreBarcodeFocus(false);
  }, [restoreBarcodeFocus, saving, scanning]);

  const addLine = (nextLine) => {
    try {
      setLines((current) => mergePurchaseLine(current, nextLine));
      setLineErrors((current) => {
        const next = { ...current };
        delete next[getPurchaseLineKey(nextLine)];
        return next;
      });
    } catch (error) {
      showToast(error.message, "error");
    }
  };

  const resolveScan = async () => {
    const scanned = barcode.trim();
    if (!scanned || scanning || saving) return;
    setScanning(true);
    try {
      const resolved = await getItemByBarcode(scanned);
      if (resolved?.requiresChoice) {
        setSharedChoice({ barcode: scanned, options: resolved.options || [] });
      } else {
        addLine(normalizeExistingLine(resolved));
      }
      setBarcode("");
    } catch (error) {
      const message = error?.response?.data?.error || error?.message || "";
      if (/not found/i.test(message)) {
        setUnknownBarcode(scanned);
        setBarcode("");
      } else {
        showToast("Barcode could not be checked. Try scanning again.", "error");
      }
    } finally {
      setScanning(false);
    }
  };

  const updateLine = (lineKey, field, value) => {
    setLines((current) => current.map((line) =>
      getPurchaseLineKey(line) === lineKey ? { ...line, [field]: value } : line,
    ));
  };

  const removeLine = (lineKey) => {
    setLines((current) => current.filter(
      (line) => getPurchaseLineKey(line) !== lineKey,
    ));
    setLineErrors((current) => {
      const next = { ...current };
      delete next[lineKey];
      return next;
    });
    barcodeRef.current?.focus();
  };

  const draftErrors = validatePurchaseDraft({ supplierId, lines });
  const canSubmit = Object.keys(draftErrors).length === 0 &&
    !supplierError && Object.keys(lineErrors).length === 0 && !saving && draftReady;
  const purchaseTotal = lines.reduce((total, line) => {
    const calculation = calculatePurchaseLine(line);
    return total + Number(calculation.lineTotal || 0);
  }, 0);

  const matchingInventory = useMemo(() => {
    const needle = manualSearch.trim().toLowerCase();
    if (!needle) return [];
    return inventoryOptions(inventory)
      .filter((option) =>
        [option.name, option.unitName, option.barcode]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(needle)),
      )
      .slice(0, 12);
  }, [inventory, manualSearch]);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const [freshInventory, activeSuppliers] = await Promise.all([
        getInventoryItems(),
        listSuppliers(),
      ]);
      setInventory(freshInventory || []);
      const currentDraft = createPurchaseDraftSnapshot({
        supplierId,
        submissionKey,
        lines,
      });
      const reconciled = reconcilePurchaseDraft({
        draft: currentDraft,
        inventory: freshInventory,
        activeSuppliers,
      });
      setSupplierError(reconciled.supplierError);
      setLineErrors(reconciled.lineErrors);
      if (reconciled.supplierError || Object.keys(reconciled.lineErrors).length) {
        showToast("Review the highlighted supplier or product before receiving.", "error");
        return;
      }

      const result = await receivePurchase({
        supplierId: Number(supplierId),
        purchasedAt: new Date().toISOString(),
        idempotencyKey: submissionKey,
        lines,
      });
      window.clearTimeout(draftSaveTimerRef.current);
      draftSaveSequenceRef.current += 1;
      try {
        window.localStorage.removeItem(PURCHASE_DRAFT_STORAGE_KEY);
      } catch {
        // The committed purchase still succeeds when private storage is unavailable.
      }
      setLines([]);
      setSupplierId("");
      setManualSearch("");
      setSupplierError("");
      setLineErrors({});
      setDraftStatus("");
      setSubmissionKey(newSubmissionKey());
      await pendingDraftSaveRef.current;
      try {
        await deletePurchaseDraft();
      } catch {
        showToast(
          "Purchase saved, but its old draft could not be cleared. It is safe to retry clearing later.",
          "warning",
        );
      }
      onInventoryChanged();
      onReceived(result);
      showToast(
        `Purchase ${result.purchaseNumber} received and stock updated.`,
        "success",
      );
      setRestoreBarcodeFocus(true);
    } catch (error) {
      showToast(
        error?.response?.data?.error ||
          "The purchase could not be received. No stock was changed.",
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  const discardDraft = async () => {
    if (!window.confirm("Discard this purchase draft and all entered items?")) return;
    window.clearTimeout(draftSaveTimerRef.current);
    draftSaveSequenceRef.current += 1;
    await pendingDraftSaveRef.current;
    try {
      await deletePurchaseDraft();
    } catch (error) {
      showToast(
        error?.response?.data?.error || "The saved purchase draft could not be deleted.",
        "error",
      );
      return;
    }
    try {
      window.localStorage.removeItem(PURCHASE_DRAFT_STORAGE_KEY);
    } catch {
      // State is still cleared; private storage may already be unavailable.
    }
    setSupplierId("");
    setLines([]);
    setManualSearch("");
    setSupplierError("");
    setLineErrors({});
    setDraftStatus("");
    setSubmissionKey(newSubmissionKey());
    setRestoreBarcodeFocus(true);
  };

  return (
    <section className="purchase-receive" aria-labelledby="purchase-receive-title">
      <header className="purchase-view-header">
        <div>
          <p className="eyebrow">PURCHASE &amp; STOCK RECEIVING</p>
          <h2 id="purchase-receive-title">Receive purchased goods</h2>
          <p>ဝယ်ယူလာသော ပစ္စည်းများကို စကင်ဖတ်ပြီး စတော့ထည့်ပါ။</p>
        </div>
      </header>

      <div className="purchase-receive-controls">
        <div className="purchase-section-heading">
          <strong>Supplier</strong>
          <span>Choose who delivered this purchase.</span>
        </div>
        <div>
          <SupplierManager
            mode="picker"
            selectedId={supplierId}
            onSelect={(nextSupplierId) => {
              setSupplierId(nextSupplierId);
              setSupplierError("");
            }}
          />
          {supplierError && <p className="form-error" role="alert">{supplierError}</p>}
        </div>
      </div>

      <div className="purchase-entry-toolbar">
        <div className="purchase-barcode-entry">
          <label htmlFor="purchase-barcode">Barcode</label>
          <div className="purchase-barcode-control">
            <input
              id="purchase-barcode"
              ref={barcodeRef}
              className="purchase-search-input"
              name="purchaseBarcode"
              value={barcode}
              onChange={(event) => setBarcode(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  resolveScan();
                }
              }}
              disabled={scanning || saving}
              autoComplete="off"
              placeholder="Scan or type barcode, then press Enter"
            />
            <button
              type="button"
              className="btn btn-primary"
              data-add-scanned-item
              onClick={resolveScan}
              disabled={!barcode.trim() || scanning || saving}
            >
              {scanning ? "Checking..." : "Add scanned item"}
            </button>
          </div>
          <small>ဘားကုဒ်စကင်ဖတ်ပါ၊ သို့မဟုတ် ရိုက်ထည့်ပြီး Enter နှိပ်ပါ။</small>
        </div>

        <details className="purchase-manual-browser">
          <summary>Find item without scanning</summary>
          <label>
            <span>Search product or barcode</span>
            <input
              className="purchase-search-input"
              type="search"
              value={manualSearch}
              onChange={(event) => setManualSearch(event.target.value)}
              placeholder="Product, unit, or barcode"
            />
          </label>
          {manualSearch && (
            <div className="purchase-manual-results">
              {matchingInventory.length ? matchingInventory.map((option) => (
                <button
                  type="button"
                  key={`${option.itemId}-${option.unitId ?? "base"}`}
                  onClick={() => {
                    addLine(normalizeExistingLine(option));
                    setManualSearch("");
                    barcodeRef.current?.focus();
                  }}
                >
                  <strong>{option.name}</strong>
                  <span>{option.unitName} · {option.conversionFactor} base unit{Number(option.conversionFactor) === 1 ? "" : "s"}</span>
                  <span>MMK {formatCurrency(option.price)}</span>
                </button>
              )) : <p>No matching products.</p>}
            </div>
          )}
        </details>
      </div>

      <div className="purchase-lines-panel">
        <div className="purchase-lines-panel-header">
          <div>
            <h3>Purchase items</h3>
            <p>Enter quantity, buying price, and selling price directly in each row.</p>
          </div>
          <span>{lines.length} product line{lines.length === 1 ? "" : "s"}</span>
        </div>
        {lines.length === 0 ? (
          <div className="purchase-lines-empty">
            <strong>No items added yet</strong>
            <p>ပထမဆုံး ပစ္စည်း၏ ဘားကုဒ်ကို စကင်ဖတ်ပါ။</p>
          </div>
        ) : (
          <PurchaseLineTable
            lines={lines}
            lineErrors={lineErrors}
            onUpdate={updateLine}
            onRemove={removeLine}
          />
        )}
      </div>

      <footer className="purchase-review-bar">
        <div>
          <span>{lines.length} product line{lines.length === 1 ? "" : "s"}</span>
          <strong>MMK {formatCurrency(purchaseTotal)}</strong>
        </div>
        <div className="purchase-draft-actions">
          {draftStatus && (
            <span className="purchase-draft-status" aria-live="polite">
              {draftStatus}
            </span>
          )}
          {lines.length > 0 && (
            <button
              type="button"
              className="btn btn-secondary"
              data-discard-purchase-draft
              disabled={saving}
              onClick={discardDraft}
            >
              Discard draft
            </button>
          )}
          <button
            type="button"
            className="btn btn-primary btn-lg"
            data-receive-purchase
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {saving ? "Receiving..." : "Receive & Save"}
          </button>
        </div>
        <small>လက်ခံပြီး စတော့ထည့်ကာ သိမ်းမည်</small>
      </footer>

      {sharedChoice && (
        <SharedBarcodePicker
          barcode={sharedChoice.barcode}
          options={sharedChoice.options}
          title="Choose receiving unit"
          description="Select the exact product and package being delivered."
          showAvailability={false}
          onSelect={(option) => {
            addLine(normalizeExistingLine(option));
            setSharedChoice(null);
            barcodeRef.current?.focus();
          }}
          onCancel={() => {
            setSharedChoice(null);
            barcodeRef.current?.focus();
          }}
        />
      )}

      {unknownBarcode && (
        <NewPurchaseItemDialog
          barcode={unknownBarcode}
          categories={categories}
          inventory={inventory}
          onConfirm={(line) => {
            addLine(line);
            setUnknownBarcode("");
          }}
          onCancel={() => {
            setUnknownBarcode("");
            barcodeRef.current?.focus();
          }}
        />
      )}
    </section>
  );
}
